import { useState } from "react";
import { api } from "../../lib/api";
import { REDUCE_MOTION } from "../../lib/theme";
import { BuzerIcon } from "../../BuzerIcons";
import { DateField } from "../../components/ui";
import { Button, Sheet, Caption } from "../../components/ui/ds";
import { fmtShortDate } from "../../lib/format";
import "./wheel.css";

// v37 — koleso šťastia po výhre v súťaži.
//
// Klient NEROZHODUJE o výhre: POST /api/wheel/spin vráti hotový výsledok (výseky + index +
// cena) a tento komponent k nemu len dotočí koleso. Reload teda výhru ZOPAKUJE, nepretočí —
// nárok je jednorazový (UNIQUE(challenge_id, user_id) v `prize_claims`).
//
// Vizuál je zámerne striedmy: kruh v odtieňoch aktuálnej témy (všetko z tokenov), jedno
// pomalé dotočenie a karta ceny. Popisky NIE SÚ vo výsekoch (na 360 px sa nedajú prečítať) —
// farby vysvetľuje legenda pod kolesom.

const SEG_DEG = 60;   // 6 výsekov
const TURNS = 4;      // koľko celých otáčok koleso spraví, než dosadne
const SPIN_MS = 3200; // musí sedieť s transition v wheel.css

// odtieň výseku podľa ceny — všetko odvodené od --brand-primary, takže koleso funguje
// v každej farebnej téme aj v dark mode
const TINTS = {
  double_points: "var(--brand-primary)",
  headwear: "var(--brand-primary-surface-strong)",
  free_day: "var(--surface-solid)",
};
const tintOf = (code) => TINTS[code] || "var(--brand-primary-surface)";

function discGradient(segments) {
  const stops = segments.map((s, i) => `${tintOf(s.code)} ${i * SEG_DEG}deg ${(i + 1) * SEG_DEG}deg`);
  // -30deg → stred nultého výseku mieri presne hore, pod ukazovateľ
  return `conic-gradient(from -${SEG_DEG / 2}deg, ${stops.join(", ")})`;
}

export function PrizeWheelModal({ wheel, onClose, reload }) {
  const [data, setData] = useState(wheel);
  const [phase, setPhase] = useState(wheel.status === "spun" ? "result" : "idle");
  const [rot, setRot] = useState(wheel.status === "spun" ? -(wheel.index || 0) * SEG_DEG : 0);
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");

  const segments = data.segments || [];
  const prize = data.prize;
  // legenda: každá cena raz, v poradí, v akom je na kolese
  const legend = segments.filter((s, i) => segments.findIndex((x) => x.code === s.code) === i);

  async function spin() {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api("/api/wheel/spin", { method: "POST" });
      const w = r.wheel;
      setData(w);
      const target = TURNS * 360 - (w.index || 0) * SEG_DEG;
      if (REDUCE_MOTION) {
        setRot(-(w.index || 0) * SEG_DEG);
        setPhase("result");
        setBusy(false);
        reload && reload();
        return;
      }
      setPhase("spinning");
      setRot(target);
      setTimeout(() => { setPhase("result"); setBusy(false); reload && reload(); }, SPIN_MS + 120);
    } catch (e) {
      setErr((e && e.message) || "Koleso sa nepodarilo zatočiť. Skús to znova.");
      setBusy(false);
    }
  }

  async function redeem() {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api("/api/wheel/redeem", { method: "POST", body: data.needs_day ? { day } : {} });
      // ZÁMERNE bez reload(): nový /api/state by nárok zhodil na null a modal by zmizol
      // aj s potvrdením. Refresh spraví rodič pri zatvorení (tlačidlo „Hotovo").
      setDone(r.message || "Cena je tvoja.");
      setPhase("done");
    } catch (e) {
      setErr((e && e.message) || "Cenu sa nepodarilo uplatniť.");
    }
    setBusy(false);
  }

  const footer = phase === "done" ? (
    <Button block onClick={onClose}>Hotovo</Button>
  ) : phase === "result" ? (
    <Button block disabled={busy || (data.needs_day && !day)} onClick={redeem}>
      {data.needs_day ? "Uplatniť cenu" : "Super, beriem!"}
    </Button>
  ) : (
    <Button block disabled={busy || phase === "spinning"} onClick={spin}>
      {phase === "spinning" ? "Točí sa…" : "Zatoč kolesom"}
    </Button>
  );

  return (
    <Sheet center icon="gift" title="Koleso výhry"
      subtitle={`Vyhral/a si súťaž „${data.challenge_name}“`}
      onClose={onClose} footer={footer}>
      {phase !== "done" && (
        <div className="bz-wheel" aria-hidden>
          <span className="bz-wheel__needle" />
          <div className={`bz-wheel__disc${REDUCE_MOTION ? " bz-wheel__disc--instant" : ""}`}
            style={{ "--bz-wheel-rot": `${rot}deg`, backgroundImage: discGradient(segments) }} />
          <span className="bz-wheel__hub">
            <BuzerIcon name="gift" size={20} color="var(--brand-primary)" />
          </span>
        </div>
      )}

      {phase === "idle" && (
        <>
          <div className="bz-wheel__legend">
            {legend.map((s) => (
              <span key={s.code} className="bz-wheel__key">
                <span className="bz-wheel__swatch" style={{ background: tintOf(s.code) }} />
                {s.label}
              </span>
            ))}
          </div>
          <Caption variant="note" style={{ marginTop: "var(--space-3)" }}>Cena je jednorazová — zatočiť sa dá raz. Platí do {fmtShortDate(data.expires_at)}</Caption>
        </>
      )}

      {phase === "result" && prize && (
        <>
          <div className="bz-wheel-prize" style={{ marginTop: "var(--space-4)" }}>
            <span className="bz-wheel-prize__icon">
              <BuzerIcon name={prize.icon} size={22} color="currentColor" />
            </span>
            <span>
              <span className="bz-wheel-prize__name">
                {data.prize_item ? data.prize_item.name : prize.label}
              </span>
              <span className="bz-wheel-prize__desc">{prize.desc}</span>
            </span>
          </div>
          {data.needs_day && (
            <div style={{ marginTop: "var(--space-4)" }}>
              <Caption icon="calendar">Vyber si deň (od zajtra, najviac {fmtShortDate(data.max_day)})</Caption>
              <div style={{ display: "flex", marginTop: 6 }}>
                <DateField value={day} onChange={setDay} min={data.min_day} max={data.max_day}
                  label="Deň, na ktorý si cenu uplatníš" />
              </div>
            </div>
          )}
        </>
      )}

      {phase === "done" && (
        <div className="bz-wheel-prize">
          <span className="bz-wheel-prize__icon">
            <BuzerIcon name="check" size={22} color="currentColor" stroke={2.6} />
          </span>
          <span><span className="bz-wheel-prize__desc" style={{ marginTop: 0 }}>{done}</span></span>
        </div>
      )}

      {err && <Caption variant="note" icon="info" style={{ color: "var(--danger)", marginTop: "var(--space-3)" }}>{err}</Caption>}
    </Sheet>
  );
}
